import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CleanKinLogo } from "@/components/Layout";
import { ArrowLeft, KeyRound, Loader2, Mail, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-in-one page: if the URL contains ?token=... it renders the reset form;
 * otherwise it renders the "email me a reset link" form. Keeps routing simple.
 */
const CleanerResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const isReset = Boolean(token);

  const [email, setEmail] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    // Reset state when switching from reset -> forgot in-place
    if (!isReset) setSent(false);
  }, [isReset]);

  const requestReset = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/cleaner/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch {
      // Even on error, show the same confirmation to avoid leaking whether the email exists.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/cleaner/reset-password`, {
        token,
        new_password: next,
      });
      toast.success("Password reset — you can sign in now.");
      navigate("/team");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAF9] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/">
            <CleanKinLogo size="default" />
          </Link>
          <h1 className="text-2xl font-bold text-[#1A2E12] mt-4">
            {isReset ? "Set a new password" : "Reset your password"}
          </h1>
          <p className="text-[#5C6B57] mt-2 text-sm">
            {isReset
              ? "Pick a new password to finish resetting your Clean Kin account."
              : "We'll email you a link to reset your Clean Kin cleaner password."}
          </p>
        </div>

        <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural">
          <CardContent className="pt-6">
            {!isReset && !sent && (
              <form onSubmit={requestReset} className="space-y-4" data-testid="forgot-password-form">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-2">
                  <Mail className="w-7 h-7 text-[#2D5016]" strokeWidth={1.5} />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#2D5016]">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="mt-1 h-11"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6"
                  data-testid="forgot-submit-btn"
                >
                  {busy ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    "Email me a reset link"
                  )}
                </Button>
              </form>
            )}

            {!isReset && sent && (
              <div className="text-center py-4 space-y-4" data-testid="forgot-password-sent">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-7 h-7 text-[#2D5016]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A2E12]">Check your inbox</h3>
                  <p className="text-sm text-[#5C6B57] mt-1">
                    If <strong>{email}</strong> is on file we&apos;ve sent a reset link. The link expires in one hour.
                  </p>
                </div>
                <p className="text-xs text-[#5C6B57]">
                  Nothing arrived? Check spam or ask the office to trigger a reset from the admin dashboard.
                </p>
              </div>
            )}

            {isReset && (
              <form onSubmit={submitReset} className="space-y-4" data-testid="reset-password-form">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-2">
                  <KeyRound className="w-7 h-7 text-[#2D5016]" strokeWidth={1.5} />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#2D5016]">New password (min. 8 chars)</Label>
                  <Input
                    type="password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    required
                    className="mt-1 h-11"
                    autoComplete="new-password"
                    data-testid="reset-new-password"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#2D5016]">Confirm new password</Label>
                  <Input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="mt-1 h-11"
                    autoComplete="new-password"
                    data-testid="reset-confirm-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6"
                  data-testid="reset-submit-btn"
                >
                  {busy ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting…</>
                  ) : (
                    "Set new password"
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#5C6B57] mt-6">
          <Link to="/team" className="text-[#2D5016] hover:underline flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to sign-in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default CleanerResetPasswordPage;
